import moment from "moment";

/**
 * Format a UTC/ISO timestamp into a human-readable string in the viewer's local
 * timezone. Returns "" when the input is missing or unparseable so callers can
 * skip rendering without extra guards.
 *
 *   Today, 11:02 AM
 *   Yesterday, 4:15 PM
 *   Jun 17, 2026, 10:35 AM
 */
export const formatMessageTimestamp = (value) => {
  if (!value && value !== 0) return "";
  // Treat backend values (e.g. createdUTC) as UTC and convert to local; this
  // matches the project-wide convention used elsewhere (e.g. ChatHistory.js).
  const m = moment.utc(value).local();
  if (!m.isValid()) return "";

  const today = moment().startOf("day");
  const yesterday = moment().subtract(1, "day").startOf("day");
  const time = m.format("h:mm A");

  if (m.isSameOrAfter(today)) return `Today, ${time}`;
  if (m.isSameOrAfter(yesterday)) return `Yesterday, ${time}`;
  return m.format("MMM D, YYYY, h:mm A");
};

/**
 * Full absolute local-time string for hover tooltips, with the viewer's
 * timezone abbreviation appended, e.g.
 *   "Wednesday, June 18, 2026, 3:42 PM IST"
 */
export const formatMessageTimestampFull = (value) => {
  if (!value && value !== 0) return "";
  const m = moment.utc(value).local();
  if (!m.isValid()) return "";
  const base = m.format("dddd, MMMM D, YYYY, h:mm A");
  const tz = getLocalTimeZoneAbbr(m.toDate());
  return tz ? `${base} ${tz}` : base;
};

// Resolve the viewer's local timezone short name (e.g. "IST", "EST", "EDT",
// "PST"). Falls back to a `GMT+offset` form when the abbreviation would be
// ambiguous. Uses the IANA zone (always unambiguous) for well-known cases
// where Intl deliberately returns the offset \u2014 notably "IST" for India,
// which Intl avoids because it collides with Israel/Ireland.
const IANA_TO_ABBR = {
  "Asia/Kolkata": "IST",
  "Asia/Calcutta": "IST",
  "Asia/Colombo": "IST",
};

export const getLocalTimeZoneAbbr = (date) => {
  try {
    const ianaName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (ianaName && IANA_TO_ABBR[ianaName]) return IANA_TO_ABBR[ianaName];
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "short",
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart?.value || "";
  } catch (_) {
    return "";
  }
};

/**
 * Short relative timestamp with timezone abbreviation appended, e.g.
 *   "Today, 11:02 AM IST"
 *   "Yesterday, 4:15 PM CDT"
 *   "Jun 17, 2026, 10:35 AM EST"
 */
export const formatMessageTimestampWithTz = (value) => {
  if (!value && value !== 0) return "";
  const m = moment.utc(value).local();
  if (!m.isValid()) return "";

  const base = formatMessageTimestamp(value);
  if (!base) return "";
  const tz = getLocalTimeZoneAbbr(m.toDate());
  return tz ? `${base} ${tz}` : base;
};

export default formatMessageTimestamp;
